import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, ExternalLink } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SearchBox } from "@/components/Pager";
import { EntryDialog, CategoriesPanel, StatsPanel, KIND_LABEL } from "@/components/glossary/AdminParts";

export default function AdminGlossaryPage() {
  const [entries, setEntries] = useState([]);
  const [cats, setCats] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [kind, setKind] = useState("ALL");
  const [q, setQ] = useState("");

  const load = async () => {
    const [e, c] = await Promise.all([api.get("/admin/glossary/entries"), api.get("/public/glossary/categories")]);
    setEntries(e.data); setCats(c.data);
  };
  useEffect(() => { load(); }, []);
  const shown = useMemo(() => entries.filter((e) => (kind === "ALL" || e.kind === kind) && e.title.toLowerCase().includes(q.toLowerCase())), [entries, kind, q]);
  const catName = (id) => cats.find((c) => c.id === id)?.name || "Kategorisiz";

  const togglePub = async (e) => {
    try { await api.put(`/admin/glossary/entries/${e.id}`, { kind: e.kind, title: e.title, category_id: e.category_id, summary: e.summary, body: e.body, published: !e.published }); load(); }
    catch (x) { toast.error(formatApiError(x.response?.data?.detail)); }
  };
  const remove = async (e) => {
    if (!window.confirm(`"${e.title}" silinsin mi?`)) return;
    await api.delete(`/admin/glossary/entries/${e.id}`); toast.success("Silindi"); load();
  };

  return (
    <div className="space-y-6" data-testid="admin-glossary-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">İçerik Yönetimi</div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Sözlük & Rehber</h1>
          <p className="text-slate-500 mt-1">Terimleri, rehberleri ve kategorileri yönetin. Yayındaki içerikler herkese açık /sozluk sayfasında görünür.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open("/sozluk", "_blank")} data-testid="glossary-open-public"><ExternalLink className="w-4 h-4 mr-2" />Herkese açık sayfa</Button>
          <Button data-testid="glossary-add-btn" onClick={() => setDialog({ entry: null })} className="bg-emerald-700 hover:bg-emerald-800"><Plus className="w-4 h-4 mr-2" />Yeni içerik</Button>
        </div>
      </div>
      <Tabs defaultValue="entries">
        <TabsList>
          <TabsTrigger value="entries" data-testid="glossary-admin-tab-entries">İçerikler ({entries.length})</TabsTrigger>
          <TabsTrigger value="cats" data-testid="glossary-admin-tab-cats">Kategoriler</TabsTrigger>
          <TabsTrigger value="stats" data-testid="glossary-admin-tab-stats">İstatistikler</TabsTrigger>
        </TabsList>
        <TabsContent value="entries" className="space-y-4 pt-3">
          <div className="flex flex-wrap gap-3 items-center">
            <SearchBox value={q} onChange={setQ} placeholder="Başlık ara…" testid="glossary-admin-search" />
            {[["ALL", "Tümü"], ["TERM", "Terimler"], ["GUIDE", "Rehberler"]].map(([k, l]) => (
              <Button key={k} size="sm" variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)} data-testid={`glossary-admin-filter-${k}`}>{l}</Button>
            ))}
          </div>
          <Card className="divide-y">
            {shown.length === 0 && <div className="p-10 text-center text-slate-500">İçerik yok.</div>}
            {shown.map((e) => (
              <div key={e.id} className="flex items-center gap-3 p-4 flex-wrap" data-testid={`glossary-admin-row-${e.slug}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold text-slate-900 flex items-center gap-2">{e.title}{e.sample && <Badge variant="outline" className="text-[10px]">Örnek içerik</Badge>}</div>
                  <div className="text-xs text-slate-500">{KIND_LABEL[e.kind]} · {catName(e.category_id)}</div>
                </div>
                <span className="text-xs text-slate-500 flex items-center gap-1 w-16"><Eye className="w-3.5 h-3.5" />{e.views || 0}</span>
                <label className="flex items-center gap-2 text-xs"><Switch checked={!!e.published} onCheckedChange={() => togglePub(e)} data-testid={`glossary-admin-publish-${e.slug}`} />{e.published ? "Yayında" : "Taslak"}</label>
                <Button size="sm" variant="ghost" onClick={() => setDialog({ entry: e })} data-testid={`glossary-admin-edit-${e.slug}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(e)} data-testid={`glossary-admin-delete-${e.slug}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
              </div>
            ))}
          </Card>
        </TabsContent>
        <TabsContent value="cats" className="pt-3"><CategoriesPanel cats={cats} entries={entries} onChanged={load} /></TabsContent>
        <TabsContent value="stats" className="pt-3"><StatsPanel /></TabsContent>
      </Tabs>
      <EntryDialog open={!!dialog} entry={dialog?.entry} cats={cats} onClose={() => setDialog(null)} onSaved={load} />
    </div>
  );
}
